import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferData, setTransferData] = useState({
    from_account: '',
    to_account: '',
    amount: '',
    description: 'Virement SEPA'
  });

  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdminOrFounder = user?.role?.role_type === 'admin' || user?.role?.role_type === 'founder';

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.get(`${API_URL}/accounts`, config);
      setAccounts(data);
      
      // Charger aussi les comptes internes de la banque si admin/founder
      if (isAdminOrFounder) {
        try {
          const bankAccountsRes = await axios.get(`${API_URL}/admin/bank-internal-accounts`, config);
          setBankAccounts(bankAccountsRes.data.accounts || []);
        } catch (err) {
          console.error('Error loading bank accounts:', err);
        }
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/transactions`, {
        ...transferData,
        amount: parseFloat(transferData.amount),
        transaction_type: 'transfer',
        currency: 'EUR'
      }, config);
      alert('Virement effectué avec succès !');
      setShowTransferForm(false);
      setTransferData({ from_account: '', to_account: '', amount: '', description: 'Virement SEPA' });
      loadAccounts();
    } catch (err) {
      alert('Erreur lors du virement: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Mes Comptes</h1>
        <button
          onClick={() => setShowTransferForm(!showTransferForm)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          {showTransferForm ? 'Annuler' : 'Nouveau virement'}
        </button>
      </div>

      {/* Transfer Form */}
      {showTransferForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Effectuer un virement</h2>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Depuis le compte
              </label>
              <select
                value={transferData.from_account}
                onChange={(e) => setTransferData({ ...transferData, from_account: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un compte</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.account_type} - {acc.iban} ({acc.balance.toFixed(2)} €)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vers le compte (IBAN)
              </label>
              <input
                type="text"
                value={transferData.to_account}
                onChange={(e) => setTransferData({ ...transferData, to_account: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="SC74 2004 1000 1234 5678 9012 3456 789"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant (€)
              </label>
              <input
                type="number"
                step="0.01"
                value={transferData.amount}
                onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={transferData.description}
                onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Valider le virement
            </button>
          </form>
        </div>
      )}

      {/* Accounts List */}
      <div className="grid gap-6">
        {accounts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">Aucun compte disponible</p>
          </div>
        ) : (
          accounts.map((account) => (
            <div
              key={account.id}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">
                    {account.account_type === 'checking' && '💳'}
                    {account.account_type === 'savings' && '💰'}
                    {account.account_type === 'investment' && '📈'}
                  </span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {account.account_type === 'checking' && 'Compte Courant'}
                      {account.account_type === 'savings' && 'Compte Épargne'}
                      {account.account_type === 'investment' && 'Compte Investissement'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        {account.status}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-gray-900">
                    {account.balance.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                  <p className="text-sm text-gray-500">{account.currency}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-600">IBAN Principal (Seychelles) 🇸🇨</p>
                  <p className="font-mono text-sm font-semibold">{account.iban || account.account_number}</p>
                  <p className="text-xs text-gray-500 mt-1">Compte bancaire principal - Offshore</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">BIC/SWIFT Seychelles</p>
                  <p className="font-mono text-sm font-semibold">{account.bic}</p>
                </div>
              </div>
              
              {account.iban_fr && (
                <div className="grid grid-cols-2 gap-4 pt-2 pb-2 border-t border-blue-100 bg-blue-50 -mx-6 px-6 mt-2">
                  <div>
                    <p className="text-xs text-blue-700 font-semibold">IBAN France (Stripe) 🇫🇷</p>
                    <p className="font-mono text-sm font-semibold text-blue-900">{account.iban_fr}</p>
                    <p className="text-xs text-blue-600 mt-1">Pour cartes virtuelles Stripe</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700 font-semibold">BIC/SWIFT France</p>
                    <p className="font-mono text-sm font-semibold text-blue-900">{account.bic_fr || 'BKBRFRP2001'}</p>
                    {account.stripe_enabled ? (
                      <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        ✅ Stripe Actif
                      </span>
                    ) : (
                      <span className="inline-block mt-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        Stripe non activé
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Bank Internal Accounts - Admin/Founder Only */}
      {isAdminOrFounder && bankAccounts.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-2xl font-bold text-gray-900">🏦 Comptes Internes de la Banque</h2>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
              Réservé Administration
            </span>
          </div>
          <div className="grid gap-6">
            {bankAccounts.map((account) => (
              <div
                key={account.id}
                className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-sm p-6 border-2 border-purple-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">
                      {account.account_type === 'reserves' && '🏛️'}
                      {account.account_type === 'capital' && '💎'}
                      {account.account_type === 'treasury' && '💰'}
                      {account.account_type === 'provisions' && '🛡️'}
                    </span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {account.name || account.account_type.toUpperCase()}
                      </h3>
                      <p className="text-sm text-gray-600">{account.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                          Compte Système
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-gray-900">
                      {account.balance.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                    <p className="text-sm text-gray-500">{account.currency}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-purple-200">
                  <div>
                    <p className="text-xs text-gray-600">Référence</p>
                    <p className="font-mono text-sm font-bold text-purple-600">{account.reference_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">IBAN</p>
                    <p className="font-mono text-sm font-semibold">{account.iban}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">BIC/SWIFT</p>
                    <p className="font-mono text-sm font-semibold">{account.bic}</p>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm p-6 border-2 border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">💼</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Total Actifs Banque</h3>
                    <p className="text-sm text-gray-600">Somme de tous les comptes internes</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold text-green-600">
                    {bankAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Accounts;
